
import AuditHistory from '@/models/AuditHistory'
import Company from '@/models/Company'
import User from '@/models/User'
import ViewModel from '@/models/ViewModel'
import {
  isArray, isEqual, isNumber, isObject, isString, split, transform, merge,
} from 'lodash'
import moment from 'moment'
import numeral from 'numeral'

import {
  Component, Prop, Ref, Vue, Watch,
} from 'vue-property-decorator'
import SelectPicker from '@/components/SelectPicker/SelectPicker.vue'
import Creative from '@/models/Creative'

@Component({
  components: {
    SelectPicker,
  },
})
export default class AuditProviderWithRelations extends ViewModel {
  @Prop()
  public result_name!: string

  @Prop()
  public table_query!: string

  // main-model
  @Prop({ required: true })
  public mainModel!: any

  @Prop({ required: true })
  public model!: any

  // Relationship array for grouping
  @Prop()
  public relationship!: string[]

  @Prop({ default: false })
  public groupView!: boolean

  @Prop({ default: 25 })
  public pageSize!: number

  @Prop({ default: false })
  public infiniteScroll!: boolean

  // Additional format dictionary to extend defaults
  @Prop({ default: () => ({}) })
  public additionalFormatDictionary!: any

  public model_instance: any = null

  public ready: boolean = false

  public history: Array<any> = []

  public page: number = 1

  public records: number = 0

  public sort: boolean = false

  public sortBy: string = 'created_at'

  public query: any = []

  public loading: boolean = false

  // Group-related data
  public active_group: string | null = null

  public active_tab_index: number = 0

  public relationship_loaded_data: any = {}

  public group_pagination: any = {}

  public loading_groups: any = {}

  public group_records: any = {}

  public default_format_dictionary: any = {
    status_duration: 'percentage',
    start_at: '[date]MM/DD/YYYY HH:mm a',
    closed_at: '[date]MM/DD/YYYY HH:mm a',
    end_at: '[date]MM/DD/YYYY HH:mm a',
    due_at: '[date]MM/DD/YYYY HH:mm a',
    flight_start: '[date]MM/DD/YYYY HH:mm a',
    flight_end: '[date]MM/DD/YYYY HH:mm a',
    sales_rep_id: 'loader',
    created_by_id: 'loader',
    sales_management_id: 'loader',
    owner_id: 'loader',
    owner: 'loader',
    agency_id: 'loader',
    advertiser_id: 'loader',
    station_id: 'loader',
    user_id: 'loader',
    account_manager_id: 'loader',
    amount: 'currency',
    percentage: 'percentage',
    original_amount: 'currency',
    updated_at: '[date]MM/DD/YYYY HH:mm a',
    status_updated_at: '[date]MM/DD/YYYY HH:mm a',
    product_revenue_distribution: 'currency',
    period_revenue_distribution: 'currency',
    monthly_revenue_distribution: 'currency',
    pipeline_value: 'currency',
    is_forecast: 'boolean:yes_no',
    net_rate: 'currency',
    net_cost: 'currency',
    creative_id: 'loader',
  }

  public get format_dictionary() {
    return merge({}, this.default_format_dictionary, this.additionalFormatDictionary)
  }

  public get requestOptions() {
    let queryParams = [...this.query]

    if (this.groupView && this.active_group) {
      queryParams.unshift(`view_group:${this.active_group}`)
    }

    let defaults: any = {
      page: this.page,
      page_size: this.pageSize,
      query: queryParams,
      sort: this.sort,
      sortBy: this.sortBy,
    }

    if (this.groupView && this.active_group) {
      defaults = {
        ...defaults,
        ...this.group_pagination[this.active_group],
      }
      // remove records from dewaults
      delete defaults.records
    }

    return {
      ...defaults,
    }
  }

  public loaded_data: any = {}

  @Watch('groupView')
  public onGroupViewChange() {
    this.initializeData()
  }

  @Watch('relationship', { deep: true })
  public onRelationshipChange() {
    if (this.groupView) {
      this.initializeGroupData()
    }
  }

  public mounted() {
    this.model_instance = this.model
    this.initializeData()
  }

  public initializeData() {
    if (this.groupView && this.relationship && this.relationship.length) {
      this.initializeGroupData()
    } else {
      this.loadHistory()
    }
  }

  public containsMorePages(rel_name: string) {
    const pagination = this.group_pagination[rel_name]
    return pagination && pagination.records > pagination.page * pagination.page_size
  }

  public initializeGroupData() {
    if (!this.relationship || !this.relationship.length) return

    this.relationship.forEach(rel => {
      Vue.set(this.relationship_loaded_data, rel, null)
      Vue.set(this.loading_groups, rel, false)

      Vue.set(this.group_records, rel, 0)

      // Group Pagination default
      // settings pagination for each group
      Vue.set(this.group_pagination, rel, {
        page: this.page,
        page_size: this.pageSize,
        sort: this.sort,
        sortBy: this.sortBy,
        records: 0,
      })
    })

    this.active_group = this.relationship[0]
    this.loadHistory()
  }

  public switchGroup(group: string) {
    this.active_group = group
    if (!this.relationship_loaded_data[group]) {
      this.loadHistory()
    }
  }

  public async loadHistory() {
    const isGroupMode = this.groupView && this.active_group

    if (isGroupMode && this.active_group) {
      Vue.set(this.loading_groups, this.active_group, true)
    } else {
      this.loading = true
    }

    try {
      const response = await this.model_instance.getHistory(this.requestOptions)

      if (response && response.data && response.data.result) {
        const results = response.data.result.audits

        // Process audits like AuditProvider does
        const audits = await Promise.all(
          results
            .map((item: any) => this.getChanges(item))
            .filter((item: any) => item.user && item.payload.length > 0),
        )

        const sortedAudits = audits.sort(
          (a, b) => new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime(),
        )

        if (isGroupMode && this.active_group) {
          let newData: any = sortedAudits

          const existingData = this.relationship_loaded_data[this.active_group] || []

          if (this.infiniteScroll) {
            newData = [...existingData, ...sortedAudits]
          }

          Vue.set(this.relationship_loaded_data, this.active_group, newData)
          Vue.set(this.group_records, this.active_group, response.data.result.records)

          Vue.set(this.group_pagination, this.active_group, {
            ...this.group_pagination[this.active_group],
            records: response.data.result.records,
          })
        } else {
          const newData = this.infiniteScroll && this.page > 1 ? [...this.history, ...sortedAudits] : sortedAudits

          this.history = newData
          this.records = response.data.result.records
        }
      } else if (isGroupMode && this.active_group) {
        Vue.set(this.relationship_loaded_data, this.active_group, [])
        Vue.set(this.group_records, this.active_group, 0)
      } else {
        this.history = []
        this.records = 0
      }
    } catch (error) {
      // console.error('Error loading history:', error)
      // Handle error state
      if (isGroupMode && this.active_group) {
        Vue.set(this.relationship_loaded_data, this.active_group, [])
        Vue.set(this.group_records, this.active_group, 0)
      } else {
        this.history = []
        this.records = 0
      }
    } finally {
      if (isGroupMode && this.active_group) {
        Vue.set(this.loading_groups, this.active_group, false)
      } else {
        this.loading = false
      }
    }
  }

  public loadMoreData(group?: string) {
    if (group) {
      this.group_pagination[group].page++
      this.loadHistory()
    } else {
      this.page++
      this.loadHistory()
    }
  }

  public onPageChange(group: string | null, page: number) {
    if (group) {
      this.group_pagination[group].page = page
      this.loadHistory()
    } else {
      this.page = page
      this.loadHistory()
    }
  }

  public formatRelationshipName(rel: string) {
    return rel.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  public formatDate(date: string) {
    return moment(date).format('MM/DD/YYYY HH:mm a')
  }

  public formatDateShort(date: string) {
    return moment(date).format('MM/DD/YY')
  }

  public formatTime(date: string) {
    return moment(date).format('HH:mm')
  }

  public injectLoadedValue(class_name: string) {
    let el = document.getElementsByClassName(class_name)
    for (let i = 0; i < el.length; i++) {
      (el[i] as HTMLElement).innerText = this.loaded_data[class_name]
    }
  }

  public loaders(id: string, type: string) {
    if (this.loaded_data.hasOwnProperty(`${type}:${id}`)) {
      this.injectLoadedValue(`${type}:${id}`)
      return
    }

    this.loaded_data[`${type}:${id}`] = 'Loading...'

    this.injectLoadedValue(`${type}:${id}`)

    if (
      [
        'sales_rep_id',
        'sales_management_id',
        'owner_id',
        'user_id',
        'account_manager_id',
        'created_by_id',
      ].includes(type)
    ) {
      User.searchOptions({ value: id }).then((options: any) => {
        let user = null

        if (options.length) {
          user = options[0]
        }

        this.loaded_data[`${type}:${id}`] = user.name || 'Unknown User'
        this.injectLoadedValue(`${type}:${id}`)
      })
    } else if (['agency_id', 'advertiser_id', 'station_id'].includes(type)) {
      Company.searchOptions({ value: id }).then((options: any) => {
        let company = null
        if (options.length) {
          company = options[0]
        }

        this.loaded_data[`${type}:${id}`] = company?.name || company?.full_name || 'Unknown Company'
        this.injectLoadedValue(`${type}:${id}`)
      })
    } else if (type === 'creative_id') {
      Creative.getCreativesByIds([id]).then((creatives: any) => {
        let creative = null
        if (creatives.length) {
          creative = creatives[0]
        }

        this.loaded_data[`${type}:${id}`] = creative?.name || 'Unknown Creative'
        this.injectLoadedValue(`${type}:${id}`)
      })
    }
  }

  public formatterList(value: any, key_name: string) {
    if (value === 'N/A') return value
    if (value === null || value === undefined) return value
    let format_type = this.format_dictionary[key_name]

    if (!format_type) return value
    if (format_type.startsWith('[date]')) {
      let format = format_type.replace('[date]', '')
      value = moment(value).format(format)
      if (value !== 'Invalid date') {
        value = value.toUpperCase()
      } else {
        value = 'N/A'
      }
    } else if (format_type === 'currency') {
      value = numeral(value).format('$0.00')
    } else if (format_type === 'toString') {
      value = JSON.parse(value)
      value = value.join(', ')
    } else if (format_type === 'percentage') {
      value = numeral(value / 100).format('0%')
    } else if (format_type === 'boolean:boolean') {
      value = value ? 'True' : 'False'
    } else if (format_type === 'boolean:yes_no') {
      value = value ? 'Yes' : 'No'
    } else if (format_type === 'boolean:int') {
      value = value ? '1' : '0'
    }

    return value
  }

  public detectAndFormatValue(value: any, key_name: string = '') {
    if (
      (isString(value) && !value.length)
      || (isArray(value) && !value.length)
      || (isObject(value) && !Object.keys(value).length)
      || value === 'N/A'
    ) {
      return null
    }

    if (!this.format_dictionary.hasOwnProperty(key_name)) {
      return value === '[]' || value === '{}' ? null : value
    }

    let format_type = this.format_dictionary[key_name]

    if (format_type === 'loader' && value && value.length && value !== 'null') {
      this.loaders(value, key_name)
    }

    return this.formatterList(value, key_name)
  }

  public clear(s: any) {
    if (s === null || s === 'null') return 'N/A'
    if (isString(s)) {
      let ss = s.replaceAll('_', ' ').replaceAll('ids', '').replaceAll('id', '').replace('00:00:00', '')
        || s
      return this.formatModelPath(ss)
    }

    return s
  }

  public valuesDidChange(newValues: any, oldValues: any) {
    // if newvalue is 0 and old is null, return false
    if (
      (newValues === 0 && oldValues === null)
      || (newValues === 'N/A' && oldValues === 'null')
      || (newValues === 'N/A' && oldValues === 'N/A')
      || (newValues === 'null' && oldValues === 'N/A')
      || (newValues === null && oldValues === 0)
      || (!newValues && !oldValues)
    ) {
      return false
    }
    return !isEqual(newValues, oldValues)
  }

  public ignored_keys = ['id', 'status_duration', 'metadata', 'metrics']

  public getChanges(item: any) {
    let direct_values: boolean = false

    let changes: any = {
      user: item.user?.name,
      event: item.event,
      changed_at: item.created_at,
      payload: [],
      id: item.id,
      auditable_id: item.auditable_id,
      auditable_type: this.formatModelPath(item.auditable_type),
    }
    let new_vals = item.new_values.map((item: any) => item.key)
    let old_vals = item.old_values.map((item: any) => item.key)
    let unique_labels = [...new Set([...new_vals, ...old_vals])]
    unique_labels = unique_labels.filter((item: any) => !this.ignored_keys.includes(item))

    // loop unique_labels
    let formated: any = {
      new: {},
      old: {},
    }
    let added_section: any = {}
    for (let label of unique_labels) {
      if (direct_values) {
        const payload = {
          key: label,
          label: this.clear(label),
          new: item.new_values.find((i: any) => i.key === label)?.value ?? null,
          old: item.old_values.find((i: any) => i.key === label)?.value ?? null,
        }

        if (this.valuesDidChange(payload.new, payload.old)) {
          changes.payload.push(payload)
        }
      } else if (label.includes(':')) {
        let splited = label.split(':')
        let section = splited[0]

        // Handle new values
        let currentNew = formated.new
        let currentOld = formated.old

        for (let i = 0; i < splited.length; i++) {
          let part = splited[i]
          if (i === splited.length - 1) {
            currentNew[part] = item.new_values.find((i: any) => i.key === label)?.value ?? null
            currentOld[part] = item.old_values.find((i: any) => i.key === label)?.value ?? null
          } else {
            if (!currentNew[part]) {
              currentNew[part] = {}
            }
            if (!currentOld[part]) {
              currentOld[part] = {}
            }
            currentNew = currentNew[part]
            currentOld = currentOld[part]
          }
        }

        if (!added_section[section]) {
          const payload = {
            key: section,
            event: item.event,
            label: this.clear(section),
            new: formated.new[section],
            old: formated.old[section],
            is_nested: true,
          }
          if (this.valuesDidChange(payload.new, payload.old)) {
            changes.payload.push(payload)
          }
          added_section[section] = true
        }
      } else {
        // If there are no colons, just set the value directly
        formated.new[label] = item.new_values.find((i: any) => i.key === label)?.value ?? 'N/A'
        formated.old[label] = item.old_values.find((i: any) => i.key === label)?.value ?? 'N/A'

        const payload = {
          key: label,
          event: item.event,
          label: this.clear(label),
          new: formated.new[label],
          old: formated.old[label],
        }
        if (label === 'percentage') {
          // check if any percentage is "N/A" or "null" and set to 0
          if (payload.new === 'N/A' || payload.new === 'null') {
            payload.new = 0
          }
          if (payload.old === 'N/A' || payload.old === 'null') {
            payload.old = 0
          }
        }

        let ignore = false
        if (label === 'owner_model_type') {
          ignore = [payload.new ?? '', payload.old ?? ''].some(s =>
            s.toLowerCase().includes(this.mainModel.name.toLowerCase()))
        }

        if (label === 'owner_model_id') {
          ignore = [payload.new ?? '', payload.old ?? ''].some(s => s === this.mainModel.id)
        }

        if (this.valuesDidChange(payload.new, payload.old) && !ignore) {
          changes.payload.push(payload)
        }
      }
    }

    return changes
  }

  public refresh(tm: number = 0) {
    setTimeout(() => {
      this.loadHistory()
    }, tm)
  }

  public setModel(instance: any) {
    Vue.set(this, 'model_instance', instance)
  }

  public dataToHTML({
    data,
    key,
    nested = false,
    history = null,
    parent_key = null,
  }: {
    data: any
    key?: any
    nested?: boolean
    history?: any
    parent_key?: any
  }): any {
    let list = [
      'sales_rep_id',
      'sales_management_id',
      'agency_id',
      'advertiser_id',
      'user_id',
      'account_manager_id',
      'owner_id',
      'owner',
      'station_id',
      'created_by_id',
      'creative_id',
    ]
    if (list.includes(key) && data && data !== 'empty' && data !== 'null') {
      this.detectAndFormatValue(data, key)
      return `<span class="text-capitalize ${key}:${data}">${data}</span>`
    }

    // add formatings here, also add extra conditions for formatting for nested objects
    if (parent_key) {
      // this.format_dictionary
      if (parent_key.includes('monthly_revenue_distribution')) {
        if (data === 'N/A') data = 0
        data = this.detectAndFormatValue(data, 'monthly_revenue_distribution')
      } else if (parent_key.includes('period_revenue_distribution')) {
        if (data === 'N/A') data = 0
        data = this.detectAndFormatValue(data, 'period_revenue_distribution')
      } else if (parent_key.includes('product_revenue_distribution')) {
        if (data === 'N/A') data = 0
        data = this.detectAndFormatValue(data, 'product_revenue_distribution')
      }
    }
    data = this.detectAndFormatValue(data, parent_key ?? key)

    if (data === null || data === 'null') data = 'N/A'
    data = this.clear(data)
    return `<span class="text-capitalize">${data}</span>`
  }

  /**
   * old_parseData is for ref only.
   * will be handy when creating the new version of this project
   */
  public old_parseData({
    data,
    key,
    nested = false,
    history = null,
  }: {
    data: any
    key?: any
    nested?: boolean
    history?: any
  }): string {
    // If data is not an object(nested), return the data
    if (!nested) return this.dataToHTML({ data, key, history })

    let elements = ''

    const formatValue = (value: any): string => {
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value)
      }
      return String(value)
    }

    const parseObject = (obj: any, parentKey: string, indentLevel: number): string => {
      let result = ''
      if (obj === 'N/A') return obj
      for (let [subKey, subValue] of Object.entries(obj)) {
        const fullKey = parentKey ? `${parentKey}.${subKey}` : subKey

        if (typeof subValue === 'object' && subValue !== null) {
          if (!isNaN(Number(subKey))) {
            subKey = '-'
          }
          // Creates a new sub item section that will contain the nested object
          result += `<p class="has-sub-items font-weight-bold text-capitalize ml-${indentLevel}" sub-items="0" section-key="${fullKey}" parent-key="${parentKey}">${this.clear(
            subKey,
          )}</p>`
          result += parseObject(subValue, fullKey, indentLevel + 1)
        } else {
          let oldVal = data.old === 'N/A' ? data.old : formatValue(data.old?.[subKey] ?? null)
          let newVal = data.new === 'N/A' ? data.new : formatValue(data.new?.[subKey] ?? null)

          if (newVal !== oldVal) {
            // increment sub-items in the parent to make result html

            newVal = this.dataToHTML({
              data: newVal,
              key: subKey,
              parent_key: parentKey,
              history,
              nested,
            })
            oldVal = this.dataToHTML({
              data: oldVal,
              key: subKey,
              parent_key: parentKey,
              history,
              nested,
            })

            if (!isNaN(Number(subKey))) {
              subKey = '-'
            }

            if (data.event === 'updated') {
              result += `<p class="ml-${indentLevel}" parent-key="${parentKey}" current-key="${subKey}">Changed <span class="font-weight-bold text-capitalize">${this.clear(
                subKey,
              )}</span> ${oldVal} <i class="mx-2 las la-long-arrow-alt-right"></i> ${newVal}</p>`
            } else {
              result += `<p class="ml-${indentLevel}" parent-key="${parentKey}" current-key="${subKey}"><span class="font-weight-bold text-capitalize">${this.clear(
                subKey,
              )}</span> ${newVal}</p>`
            }
          }
        }
      }

      return result
    }

    // creates a new section
    elements += `<p class="font-weight-bold text-capitalize main-section has-sub-items" sub-items="0" section-key="${key}"> ${this.clear(
      key,
    )}</p>`
    elements += parseObject(data.new ?? [], key, 1)

    elements = this.updateSubItem(elements)

    return elements
  }

  public parseData({
    data,
    key,
    nested = false,
    history = null,
  }: {
    data: any
    key?: any
    nested?: boolean
    history?: any
  }): string {
    // If data is not an object(nested), return the data
    if (!nested) {
      return this.dataToHTML({ data, key, history })
    }

    let elements = ''

    const formatValue2 = (value: any): string => {
      if (value === null || value === undefined) {
        return 'N/A'
      }

      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : 'N/A'
      }

      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value)
      }

      return String(value)
    }

    const parseObject2 = (
      newObj: any,
      oldObj: any,
      parentKey: string,
      indentLevel: number,
      currentPath: string[] = [],
    ): string => {
      let result = ''

      if (newObj === 'N/A' && oldObj === 'N/A') {
        return result
      }

      // Handle arrays
      if (Array.isArray(newObj) || Array.isArray(oldObj)) {
        const newArray = Array.isArray(newObj) ? newObj : []
        const oldArray = Array.isArray(oldObj) ? oldObj : []
        const maxLength = Math.max(newArray.length, oldArray.length)

        for (let i = 0; i < maxLength; i++) {
          const newVal = i < newArray.length ? newArray[i] : 'N/A'
          const oldVal = i < oldArray.length ? oldArray[i] : 'N/A'
          const arrayKey = i.toString()
          const fullPath = [...currentPath, arrayKey]

          if (typeof newVal === 'object' && newVal !== null) {
            result += `<p class="has-sub-items font-weight-bold text-capitalize ml-${indentLevel}" sub-items="0" section-key="${fullPath.join(
              '.',
            )}" parent-key="${parentKey}">-</p>`
            result += parseObject2(newVal, oldVal, fullPath.join('.'), indentLevel + 1, fullPath)
          } else if (newVal !== oldVal) {
            const formattedNewVal = this.dataToHTML({
              data: formatValue2(newVal),
              key: arrayKey,
              parent_key: parentKey,
              history,
              nested: true,
            })
            const formattedOldVal = this.dataToHTML({
              data: formatValue2(oldVal),
              key: arrayKey,
              parent_key: parentKey,
              history,
              nested: true,
            })

            if (data.event === 'updated') {
              result += `<p class="ml-${indentLevel}" parent-key="${parentKey}" current-key="${arrayKey}">Changed <span class="font-weight-bold text-capitalize">-</span> ${formattedOldVal} <i class="mx-2 las la-long-arrow-alt-right"></i> ${formattedNewVal}</p>`
            } else {
              result += `<p class="ml-${indentLevel}" parent-key="${parentKey}" current-key="${arrayKey}"><span class="font-weight-bold text-capitalize">-</span> ${formattedNewVal}</p>`
            }
          }
        }

        return result
      }

      // Handle objects
      const newObjKeys = newObj && typeof newObj === 'object' ? Object.keys(newObj) : []
      const oldObjKeys = oldObj && typeof oldObj === 'object' ? Object.keys(oldObj) : []
      const allKeys = [...new Set([...newObjKeys, ...oldObjKeys])]

      for (const subKey of allKeys) {
        const fullPath = [...currentPath, subKey]
        const fullKey = fullPath.join('.')
        const newSubValue = newObj && typeof newObj === 'object' ? newObj[subKey] : 'N/A'
        const oldSubValue = oldObj && typeof oldObj === 'object' ? oldObj[subKey] : 'N/A'

        if (
          (typeof newSubValue === 'object' && newSubValue !== null)
          || (typeof oldSubValue === 'object' && oldSubValue !== null)
        ) {
          let displayKey = subKey
          if (!isNaN(Number(subKey))) {
            displayKey = `Item ${parseInt(subKey) + 1}`
          }

          // Creates a new sub item section that will contain the nested object
          result += `<p class="has-sub-items font-weight-bold text-capitalize ml-${indentLevel}" sub-items="0" section-key="${fullKey}" parent-key="${parentKey}">${this.clear(
            displayKey,
          )}</p>`
          result += parseObject2(newSubValue, oldSubValue, fullKey, indentLevel + 1, fullPath)
        } else {
          // Handle primitive values
          const formattedNewVal = formatValue2(newSubValue)
          const formattedOldVal = formatValue2(oldSubValue)

          if (formattedNewVal !== formattedOldVal) {
            const processedNewVal = this.dataToHTML({
              data: formattedNewVal,
              key: subKey,
              parent_key: parentKey,
              history,
              nested: true,
            })
            const processedOldVal = this.dataToHTML({
              data: formattedOldVal,
              key: subKey,
              parent_key: parentKey,
              history,
              nested: true,
            })

            let displayKey = subKey
            if (!isNaN(Number(subKey))) {
              displayKey = `Item ${parseInt(subKey) + 1}`
            }

            if (data.event === 'updated') {
              result += `<p class="ml-${indentLevel}" parent-key="${parentKey}" current-key="${subKey}">Changed <span class="font-weight-bold text-capitalize">${this.clear(
                displayKey,
              )}</span> ${processedOldVal} <i class="mx-2 las la-long-arrow-alt-right"></i> ${processedNewVal}</p>`
            } else {
              result += `<p class="ml-${indentLevel}" parent-key="${parentKey}" current-key="${subKey}"><span class="font-weight-bold text-capitalize">${this.clear(
                displayKey,
              )}</span> ${processedNewVal}</p>`
            }
          }
        }
      }

      return result
    }

    // creates a new section
    elements += `<p class="font-weight-bold text-capitalize main-section has-sub-items" sub-items="0" section-key="${key}"> ${this.clear(
      key,
    )}</p>`

    elements += parseObject2(data.new ?? 'N/A', data.old ?? 'N/A', key, 1, [])

    elements = this.updateSubItem2(elements)

    return elements
  }

  public formatModelPath(name: string) {
    if (!isString(name)) return name
    return name.replace('App\\Models\\', '')
  }

  public updateSubItem(html: string) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const sections = doc.querySelectorAll('.has-sub-items')
    sections.forEach(section => {
      const sectionKey = section.getAttribute('section-key')
      const subItems = doc.querySelectorAll(`[parent-key="${sectionKey}"]`)
      section.setAttribute('sub-items', String(subItems.length || 0))
    })

    const subItems = doc.querySelectorAll('.has-sub-items')
    subItems.forEach(item => {
      const subItemsCount = item.getAttribute('sub-items')
      if (subItemsCount === '0') {
        const parent = item.parentElement
        if (parent) {
          const filtered = Array.from(parent.children).filter(
            child =>
              !child.classList.contains('has-sub-items') || child.getAttribute('sub-items') !== '0',
          )
          item.setAttribute('sub-items', String(filtered.length))
        }
      }
    })

    const elements = doc.querySelectorAll('.has-sub-items')
    elements.forEach(element => {
      const subItemsCount = element.getAttribute('sub-items')
      if (subItemsCount === '0') {
        element.remove()
      }
    })

    return doc.body.innerHTML
  }

  public updateSubItem2(html: string) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const sections = doc.querySelectorAll('.has-sub-items')

    sections.forEach(section => {
      const sectionKey = section.getAttribute('section-key')
      const subItems = doc.querySelectorAll(`[parent-key="${sectionKey}"]`)

      section.setAttribute('sub-items', String(subItems.length || 0))
    })

    const subItems = doc.querySelectorAll('.has-sub-items')
    subItems.forEach(item => {
      const subItemsCount = item.getAttribute('sub-items')

      if (subItemsCount === '0') {
        const parent = item.parentElement
        if (parent) {
          const filtered = Array.from(parent.children).filter(
            child =>
              !child.classList.contains('has-sub-items') || child.getAttribute('sub-items') !== '0',
          )
          item.setAttribute('sub-items', String(filtered.length))
        }
      }
    })

    const elements = doc.querySelectorAll('.has-sub-items')
    elements.forEach(element => {
      const subItemsCount = element.getAttribute('sub-items')
      if (subItemsCount === '0') {
        element.remove()
      }
    })

    const finalResult = doc.body.innerHTML

    return finalResult
  }

  // Default table pagination options
  public get pagination_options(): any {
    let ret = [
      { name: '2', value: '2' },
      { name: '10', value: '10' },
      { name: '25', value: '25' },
      { name: '50', value: '50' },
      { name: '100', value: '100' },
    ]

    return ret
  }
}
